import type { Stochastic } from '../../schemas/indicatortypes.ts';

// Momentum Indicators
export class MomentumIndicators {
    /**
     * Calculate RSI (Relative Strength Index)
     */
    static calculateRSI(data: number[], period = 14): number[] {
        if (data.length < period + 1) return [];

        const changes: number[] = [];
        for (let i = 1; i < data.length; i++) {
            changes.push(data[i] - data[i - 1]);
        }

        const gains = changes.map((change) => (change > 0 ? change : 0));
        const losses = changes.map((change) => (change < 0 ? Math.abs(change) : 0));

        let avgGain = gains.slice(0, period).reduce((a: number, b: number) => a + b, 0) / period;
        let avgLoss = losses.slice(0, period).reduce((a: number, b: number) => a + b, 0) / period;

        const rsi: number[] = [];

        for (let i = period; i < changes.length; i++) {
            const rs = avgGain / avgLoss;
            rsi.push(100 - 100 / (1 + rs));

            avgGain = (avgGain * (period - 1) + gains[i]) / period;
            avgLoss = (avgLoss * (period - 1) + losses[i]) / period;
        }

        return rsi;
    }

    /**
     * Calculate Stochastic Oscillator
     */
    static calculateStochastic(prices: number[], highs: number[], lows: number[]): Stochastic[] {
        const stochastic: Stochastic[] = [];
        const period = 14;
        const smoothK = 3;
        const smoothD = 3;

        if (prices.length < period) return [];

        // Calculate %K
        const percentK: number[] = [];
        for (let i = period - 1; i < prices.length; i++) {
            const high = Math.max(...highs.slice(i - period + 1, i + 1));
            const low = Math.min(...lows.slice(i - period + 1, i + 1));
            const close = prices[i];

            const k = ((close - low) / (high - low)) * 100;
            percentK.push(k);
        }

        // Smooth %K
        const smoothedK: number[] = [];
        for (let i = smoothK - 1; i < percentK.length; i++) {
            const sum = percentK.slice(i - smoothK + 1, i + 1).reduce((a, b) => a + b, 0);
            smoothedK.push(sum / smoothK);
        }

        // Calculate %D (smooth of %K)
        for (let i = smoothD - 1; i < smoothedK.length; i++) {
            const sum = smoothedK.slice(i - smoothD + 1, i + 1).reduce((a, b) => a + b, 0);
            const d = sum / smoothD;

            stochastic.push({
                k: smoothedK[i],
                d: d,
            });
        }

        return stochastic;
    }

    /**
     * Calculate CCI (Commodity Channel Index)
     */
    static calculateCCI(prices: number[], highs: number[], lows: number[]): number[] {
        const cci: number[] = [];
        const period = 20;

        if (prices.length < period) return [];

        for (let i = period - 1; i < prices.length; i++) {
            const slice = prices.slice(i - period + 1, i + 1);
            const typicalPrices = slice.map((price, idx) => {
                const high = highs[i - period + 1 + idx] || price;
                const low = lows[i - period + 1 + idx] || price;
                return (high + low + price) / 3;
            });

            const sma = typicalPrices.reduce((a, b) => a + b, 0) / period;
            const meanDeviation = typicalPrices.reduce((sum, tp) => sum + Math.abs(tp - sma), 0) / period;

            const currentTP = (highs[i] + lows[i] + prices[i]) / 3;
            const cciValue = meanDeviation !== 0 ? (currentTP - sma) / (0.015 * meanDeviation) : 0;

            cci.push(cciValue);
        }

        return cci;
    }

    /**
     * Calculate Rate of Change
     */
    static calculateROC(prices: number[]): number[] {
        const roc: number[] = [];
        for (let i = 10; i < prices.length; i++) {
            roc.push(((prices[i] - prices[i - 10]) / prices[i - 10]) * 100);
        }
        return roc;
    }

    /**
     * Calculate Williams %R
     */
    static calculateWilliamsR(prices: number[]): number[] {
        const williamsR: number[] = [];
        const period = 14;

        if (prices.length < period) return [];

        for (let i = period - 1; i < prices.length; i++) {
            const slice = prices.slice(i - period + 1, i + 1);
            const high = Math.max(...slice);
            const low = Math.min(...slice);
            const close = prices[i];

            const wr = ((high - close) / (high - low)) * -100;
            williamsR.push(wr);
        }

        return williamsR;
    }

    /**
     * Calculate Momentum
     */
    static calculateMomentum(prices: number[]): number[] {
        const momentum: number[] = [];
        for (let i = 10; i < prices.length; i++) {
            momentum.push(prices[i] - prices[i - 10]);
        }
        return momentum;
    }
}
